import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  List,
  Row,
  Select,
  Spin,
  Typography,
  message
} from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { errorText, openBlob } from "../api";
import { formatDate } from "../shared/money";
import { createEmployeeDocument, employeeDocumentBlob, listEmployeeDocuments } from "./api";
import { useEmployees } from "./Lohnkonto";
import PageToolbar from "../../../../design-system/PageToolbar";

const EmployeeDocuments = () => {
  const { t } = useTranslation("accounting");
  const employees = useEmployees();
  const [employeeId, setEmployeeId] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [entgeltForm] = Form.useForm();
  const [arbeitForm] = Form.useForm();

  const load = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const res = await listEmployeeDocuments(employeeId);
      setDocs(res.data.documents || []);
    } catch (err) {
      message.error(errorText(err));
    }
    setLoading(false);
  }, [employeeId]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (kind, body) => {
    setLoading(true);
    try {
      const res = await createEmployeeDocument(employeeId, kind, body);
      message.success(
        res.data.note ? `${t("Document created")} – ${res.data.note}` : t("Document created")
      );
      await load();
    } catch (err) {
      message.error(errorText(err));
      setLoading(false);
    }
  };

  const submitEntgelt = async () => {
    const v = await entgeltForm.validateFields();
    create("entgeltbescheinigung", {
      from: v.range[0].startOf("month").format("YYYY-MM-DD"),
      to: v.range[1].endOf("month").format("YYYY-MM-DD"),
      purpose: v.purpose || ""
    });
  };
  const submitArbeit = async () => {
    const v = await arbeitForm.validateFields();
    create("arbeitsbescheinigung", {
      exitDate: v.exitDate.format("YYYY-MM-DD"),
      reason: v.reason || "",
      noticeDate: v.noticeDate ? v.noticeDate.format("YYYY-MM-DD") : "",
      noticeBy: v.noticeBy || ""
    });
  };

  return (
    <Spin spinning={loading}>
      <PageToolbar
        filters={
          <Select
            showSearch
            placeholder={t("Employee")}
            aria-label={t("Employee")}
            optionFilterProp="label"
            value={employeeId}
            onChange={setEmployeeId}
            options={employees.map(e => ({
              value: e.id,
              label: `${e.name}${e.employeeId ? ` (${e.employeeId})` : ""}`
            }))}
          />
        }
      />
      <Card size="small" title={t("Certificates")}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card type="inner" size="small" title={t("Entgeltbescheinigung")}>
              <Typography.Paragraph type="secondary">
                {t(
                  "Gross and net per month for a Krankenkasse, Elterngeld or Wohngeld application (usually the last three months)."
                )}
              </Typography.Paragraph>
              <Form
                form={entgeltForm}
                layout="vertical"
                initialValues={{
                  range: [dayjs().subtract(3, "month"), dayjs().subtract(1, "month")]
                }}
              >
                <Form.Item name="range" label={t("Months")} rules={[{ required: true }]}>
                  <DatePicker.RangePicker picker="month" style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item name="purpose" label={t("Purpose")}>
                  <Input placeholder={t("e.g. Elterngeld")} />
                </Form.Item>
                <Button type="primary" disabled={!employeeId} onClick={submitEntgelt}>
                  {t("Create PDF")}
                </Button>
              </Form>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card type="inner" size="small" title={t("Arbeitsbescheinigung")}>
              <Typography.Paragraph type="secondary">
                {t(
                  "Employment dates, the last twelve months of pay and the reason for leaving, for the Agentur für Arbeit (§ 312 SGB III)."
                )}
              </Typography.Paragraph>
              <Form
                form={arbeitForm}
                layout="vertical"
                initialValues={{ exitDate: dayjs().endOf("month") }}
              >
                <Form.Item name="exitDate" label={t("Exit date")} rules={[{ required: true }]}>
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item name="reason" label={t("Reason for leaving")}>
                  <Input placeholder={t("e.g. Kündigung durch Arbeitnehmer, Befristung")} />
                </Form.Item>
                <Row gutter={[16, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="noticeDate" label={t("Notice given on")}>
                      <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="noticeBy" label={t("Notice given by")}>
                      <Select
                        allowClear
                        options={[
                          { value: "Arbeitgeber", label: t("Employer") },
                          { value: "Arbeitnehmer", label: t("Employee") }
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <div>
                  <Button type="primary" disabled={!employeeId} onClick={submitArbeit}>
                    {t("Create PDF")}
                  </Button>
                </div>
              </Form>
            </Card>
          </Col>
        </Row>
        <List
          style={{ marginTop: 16 }}
          size="small"
          header={<strong>{t("Generated documents")}</strong>}
          dataSource={docs}
          locale={{
            emptyText: employeeId
              ? t("No documents for this employee yet.")
              : t("Choose an employee.")
          }}
          renderItem={doc => (
            <List.Item
              actions={[
                <Button
                  key="dl"
                  onClick={() =>
                    openBlob(employeeDocumentBlob(employeeId, doc._id), doc.name).catch(err =>
                      message.error(errorText(err))
                    )
                  }
                >
                  {t("Download")}
                </Button>
              ]}
            >
              <List.Item.Meta
                title={<span className="acct-wrap">{doc.name}</span>}
                description={`${formatDate(doc.createdAt)} · ${doc.description?.replace(/\s*\[payroll-doc:[^\]]*\]/, "") || ""}`}
              />
            </List.Item>
          )}
        />
      </Card>
    </Spin>
  );
};

export default EmployeeDocuments;
